import { questionnaireService } from '../services/questionnaireService';

/**
 * Scans a questionnaire definition for {SCORE_NAME} placeholders and
 * calculates those scores via the API.
 * Only scans sections up to (and including) upToSectionIndex to avoid
 * calculating scores for sections the user hasn't reached yet.
 */
export const calculateRequiredScores = async (
  questionnaireDefinition: any,
  questionnaireToken: string | null,
  upToSectionIndex?: number
): Promise<Record<string, any>> => {
  if (!questionnaireToken || !questionnaireDefinition?.sections) {
    return {};
  }

  const scorePlaceholderPattern = /\{([A-Z_0-9]+)\}/g;
  const scoreNamesToCalculate = new Set<string>();

  const sections = questionnaireDefinition.sections;
  const limit = upToSectionIndex !== undefined ? upToSectionIndex + 1 : sections.length;

  sections.slice(0, limit).forEach((section: any) => {
    section.fields.forEach((field: any) => {
      if (field.htmlContent) {
        const matches = Array.from(field.htmlContent.matchAll(scorePlaceholderPattern)) as RegExpMatchArray[];
        matches.forEach((match) => scoreNamesToCalculate.add(match[1]));
      }
      if (field.content) {
        const matches = Array.from(field.content.matchAll(scorePlaceholderPattern)) as RegExpMatchArray[];
        matches.forEach((match) => scoreNamesToCalculate.add(match[1]));
      }
      if (field.label) {
        const matches = Array.from(field.label.matchAll(scorePlaceholderPattern)) as RegExpMatchArray[];
        matches.forEach((match) => scoreNamesToCalculate.add(match[1]));
      }
    });
  });

  const calculatedScores: Record<string, any> = {};

  for (const scoreName of Array.from(scoreNamesToCalculate)) {
    try {
      const scoreResponse = await questionnaireService.calculateScore(questionnaireToken, scoreName);

      // Main score entry — {ASRS} → "High"
      calculatedScores[scoreResponse.score_name] = {
        score: scoreResponse.score,
        score_groups: scoreResponse.score_groups,
      };

      // Also expose each section score as an uppercase top-level entry so it can be
      // referenced as a {PLACEHOLDER} in content, e.g. {ASRS_PART_A} → "5".
      if (scoreResponse.score_groups) {
        for (const [groupName, groupValue] of Object.entries(scoreResponse.score_groups)) {
          calculatedScores[groupName.toUpperCase()] = { score: groupValue };
        }
      }
    } catch (err) {
      console.error(`[calculateRequiredScores] Failed to calculate score: ${scoreName}`, err);
    }
  }

  return calculatedScores;
};
